import React from 'react';
import { useLanguage, Language } from '../contexts/LanguageContext';
import { LegalPage, LegalDoc } from '../components/LegalContent';

// EN/ES finalized (drafted 2026-09-21, approved by Manuel). HT/VI = best
// effort machine translation, pending native review — same convention used
// for the rest of the site's HT/VI content.
const DOCS: Record<Language, LegalDoc> = {
  es: {
    title: 'Política de Privacidad',
    updated: 'Última actualización: 21 de septiembre de 2026',
    intro: 'Noor Passport Services (operado por Noor Metabolico LLC, Florida) respeta su privacidad. Esta política explica qué información recolectamos, cómo la usamos y con quién la compartimos cuando usted usa nuestro sitio o nuestro servicio de preparación de formularios de pasaporte.',
    sections: [{
      heading: 'Quiénes somos',
      blocks: [{
        type: 'p',
        text: 'Noor Passport Services es un negocio privado que ayuda a personas de habla española, kreyòl (criollo haitiano), inglés y vietnamita, en el suroeste de Florida, a completar sus propios formularios de pasaporte en su idioma. **No somos una agencia de gobierno, ni un despacho de abogados, ni notarios**, y no damos asesoría legal.'
      }]
    }, {
      heading: 'Qué información recolectamos',
      blocks: [{
        type: 'ul',
        items: ['**Datos de contacto:** nombre, teléfono, correo electrónico, y el idioma en el que prefiere comunicarse.', '**Datos del formulario:** la información que usted escribe en el cuestionario para preparar su formulario oficial (nombre legal, fecha y lugar de nacimiento, dirección, y datos similares tal como aparecen en sus documentos de identidad).', '**Documentos que usted sube:** copias de documentos de identidad o actas que nos entrega para preparar su formulario o validar su foto.', '**Foto de pasaporte** (si contrata ese servicio adicional): la imagen que usted sube para validación.', '**Datos de pago:** el pago se procesa directo por Stripe; Noor no almacena números de tarjeta.', '**Datos de uso del sitio y del asistente:** mensajes que usted envía por WhatsApp o por el asistente virtual del sitio.']
      }]
    }, {
      heading: 'Cómo usamos su información',
      blocks: [{
        type: 'p',
        text: 'Usamos su información únicamente para: preparar el formulario que usted nos pidió, comunicarnos con usted sobre su caso, procesar su pago, y validar su foto de pasaporte si contrató ese servicio. No vendemos su información a terceros ni la usamos para publicidad dirigida.'
      }]
    }, {
      heading: 'Con quién compartimos su información',
      blocks: [{
        type: 'p',
        text: 'Compartimos datos únicamente con los proveedores que hacen posible el servicio, cada uno bajo sus propias políticas de seguridad:'
      }, {
        type: 'table',
        headers: ['Proveedor', 'Para qué lo usamos'],
        rows: [['Fillout', 'Recibir el contrato firmado, el pago y las respuestas del cuestionario'], ['Supabase', 'Guardar de forma segura los datos de su caso (es nuestra fuente única de datos)'], ['Stripe', 'Procesar el pago; Noor nunca ve el número completo de su tarjeta'], ['Un validador de fotos (ej. Snap2Pass)', 'Validar que su foto cumple las reglas oficiales, si contrató ese servicio'], ['Twilio y ElevenLabs', 'Operar el asistente virtual y la línea de WhatsApp']]
      }, {
        type: 'p',
        text: 'No compartimos su información con el gobierno de EE. UU. más allá de lo que usted mismo envía en su propio trámite; Noor no presenta trámites en su nombre ante ninguna agencia.'
      }]
    }, {
      heading: 'Menores de edad',
      blocks: [{
        type: 'p',
        text: 'Muchos trámites de pasaporte son para niños. Cuando el caso es de un menor, es el padre, madre o tutor legal quien completa el cuestionario y sube los documentos en su nombre. No recolectamos información directamente de menores.'
      }]
    }, {
      heading: 'Cuánto tiempo guardamos su información',
      blocks: [{
        type: 'p',
        text: 'Conservamos los datos de su caso mientras dure el servicio y por un tiempo razonable después, por si necesita la garantía de corrección gratis. Puede pedir que eliminemos su información una vez terminado el servicio, escribiendo a info@noorpassportservices.com.'
      }]
    }, {
      heading: 'Sus derechos',
      blocks: [{
        type: 'p',
        text: 'Puede pedirnos en cualquier momento: ver qué información tenemos de usted, corregirla, o pedir que la eliminemos (salvo lo que debamos conservar por ley). Escríbanos a info@noorpassportservices.com en el idioma de su preferencia.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  en: {
    title: 'Privacy Policy',
    updated: 'Last updated: September 21, 2026',
    intro: 'Noor Passport Services (operated by Noor Metabolico LLC, Florida) respects your privacy. This policy explains what information we collect, how we use it, and who we share it with when you use our site or our passport form-preparation service.',
    sections: [{
      heading: 'Who we are',
      blocks: [{
        type: 'p',
        text: 'Noor Passport Services is a private business that helps Spanish-, Haitian Creole-, English-, and Vietnamese-speaking people in southwest Florida complete their own passport forms in their language. **We are not a government agency, a law firm, or notaries**, and we do not give legal advice.'
      }]
    }, {
      heading: 'Information we collect',
      blocks: [{
        type: 'ul',
        items: ['**Contact information:** name, phone number, email, and your preferred language.', '**Form data:** the information you enter in the questionnaire to prepare your official form (legal name, date and place of birth, address, and similar details as they appear on your ID documents).', '**Documents you upload:** copies of ID documents or certificates you give us to prepare your form or validate your photo.', '**Passport photo** (if you purchase that add-on): the image you upload for validation.', '**Payment data:** payment is processed directly by Stripe; Noor does not store card numbers.', "**Site and assistant usage data:** messages you send via WhatsApp or the site's virtual assistant."]
      }]
    }, {
      heading: 'How we use your information',
      blocks: [{
        type: 'p',
        text: 'We use your information only to: prepare the form you requested, communicate with you about your case, process your payment, and validate your passport photo if you purchased that service. We do not sell your information to third parties or use it for targeted advertising.'
      }]
    }, {
      heading: 'Who we share your information with',
      blocks: [{
        type: 'p',
        text: 'We share data only with the providers that make the service possible, each under its own security policies:'
      }, {
        type: 'table',
        headers: ['Provider', 'What we use it for'],
        rows: [['Fillout', 'Receiving the signed contract, payment, and questionnaire answers'], ['Supabase', 'Securely storing your case data (our single source of data)'], ['Stripe', 'Processing payment; Noor never sees your full card number'], ['A photo validator (e.g. Snap2Pass)', 'Validating that your photo meets the official rules, if you purchased that service'], ['Twilio and ElevenLabs', 'Operating the virtual assistant and WhatsApp line']]
      }, {
        type: 'p',
        text: 'We do not share your information with the U.S. government beyond what you submit yourself in your own application; Noor does not file applications on your behalf with any agency.'
      }]
    }, {
      heading: 'Minors',
      blocks: [{
        type: 'p',
        text: "Many passport applications are for children. When a case is for a minor, the parent or legal guardian completes the questionnaire and uploads the documents on the minor's behalf. We do not collect information directly from minors."
      }]
    }, {
      heading: 'How long we keep your information',
      blocks: [{
        type: 'p',
        text: 'We keep your case data for as long as the service lasts and for a reasonable time afterward, in case you need the free-correction guarantee. You may ask us to delete your information once the service is complete by writing to info@noorpassportservices.com.'
      }]
    }, {
      heading: 'Your rights',
      blocks: [{
        type: 'p',
        text: 'You may ask us at any time to: see what information we have about you, correct it, or request that we delete it (except what we must keep by law). Write to us at info@noorpassportservices.com in your preferred language.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  ht: {
    title: 'Politik Konfidansyalite',
    updated: 'Dènye mizajou: 21 septanm 2026',
    intro: 'Noor Passport Services (opere pa Noor Metabolico LLC, Florida) respekte vi prive ou. Politik sa a eksplike ki enfòmasyon nou kolekte, kijan nou itilize yo, epi ak ki moun nou pataje yo lè ou itilize sit nou an oswa sèvis preparasyon fòm paspò nou an.',
    sections: [{
      heading: 'Kiyès nou ye',
      blocks: [{
        type: 'p',
        text: 'Noor Passport Services se yon biznis prive ki ede moun ki pale panyòl, kreyòl ayisyen, anglè, ak vyetnamyen, nan sidwès Florid, ranpli pwòp fòm paspò yo nan lang yo. **Nou pa yon ajans gouvènman, ni yon kabinè avoka, ni notè**, epi nou pa bay konsèy legal.'
      }]
    }, {
      heading: 'Ki enfòmasyon nou kolekte',
      blocks: [{
        type: 'ul',
        items: ['**Enfòmasyon kontak:** non, telefòn, imèl, ak lang ou pi pito pou kominike.', '**Done fòm nan:** enfòmasyon ou ekri nan kesyonè a pou prepare fòm ofisyèl ou (non legal, dat ak kote nesans, adrès, ak lòt detay menm jan yo parèt sou dokiman idantite ou).', '**Dokiman ou telechaje:** kopi dokiman idantite oswa sètifika ou ban nou pou prepare fòm ou oswa valide foto ou.', '**Foto paspò** (si ou achte sèvis adisyonèl sa a): imaj ou telechaje pou validasyon.', '**Done peman:** peman an trete dirèkteman pa Stripe; Noor pa estoke nimewo kat.', '**Done itilizasyon sit ak asistan an:** mesaj ou voye sou WhatsApp oswa asistan vityèl sit la.']
      }]
    }, {
      heading: 'Kijan nou itilize enfòmasyon ou',
      blocks: [{
        type: 'p',
        text: 'Nou itilize enfòmasyon ou sèlman pou: prepare fòm ou te mande a, kominike avè ou sou ka ou, trete peman ou, epi valide foto paspò ou si ou te achte sèvis sa a. Nou pa vann enfòmasyon ou bay twazyèm pati ni nou pa itilize li pou piblisite sible.'
      }]
    }, {
      heading: 'Ak ki moun nou pataje enfòmasyon ou',
      blocks: [{
        type: 'p',
        text: 'Nou pataje done sèlman ak founisè ki fè sèvis la posib, chak anba pwòp politik sekirite yo:'
      }, {
        type: 'table',
        headers: ['Founisè', 'Pou ki sa nou itilize li'],
        rows: [['Fillout', 'Resevwa kontra ki siyen an, peman an, ak repons kesyonè a'], ['Supabase', 'Konsève done ka ou an sekirite (se sèl sous done nou)'], ['Stripe', 'Trete peman an; Noor pa janm wè nimewo kat konplè ou'], ['Yon validatè foto (pa egzanp Snap2Pass)', 'Valide foto ou respekte règ ofisyèl yo, si ou te achte sèvis sa a'], ['Twilio ak ElevenLabs', 'Fè asistan vityèl la ak liy WhatsApp la mache']]
      }, {
        type: 'p',
        text: 'Nou pa pataje enfòmasyon ou ak gouvènman ameriken an pi lwen pase sa ou menm ou soumèt nan pwòp aplikasyon ou; Noor pa soumèt aplikasyon nan non ou devan okenn ajans.'
      }]
    }, {
      heading: 'Minè',
      blocks: [{
        type: 'p',
        text: 'Anpil aplikasyon paspò se pou timoun. Lè ka a se pou yon minè, se paran an oswa gadyen legal la ki ranpli kesyonè a epi telechaje dokiman yo nan non minè a. Nou pa kolekte enfòmasyon dirèkteman nan men minè.'
      }]
    }, {
      heading: 'Konbyen tan nou konsève enfòmasyon ou',
      blocks: [{
        type: 'p',
        text: 'Nou konsève done ka ou pandan tout sèvis la epi pou yon tan rezonab apre, nan ka ou ta bezwen garanti korije gratis la. Ou ka mande nou efase enfòmasyon ou yon fwa sèvis la fini, ekri nou nan info@noorpassportservices.com.'
      }]
    }, {
      heading: 'Dwa ou yo',
      blocks: [{
        type: 'p',
        text: 'Ou ka mande nou nenpòt lè: wè ki enfòmasyon nou genyen sou ou, korije yo, oswa mande nou efase yo (eksepte sa lalwa oblije nou konsève). Ekri nou nan info@noorpassportservices.com nan lang ou pi pito.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  },
  vi: {
    title: 'Chính Sách Bảo Mật',
    updated: 'Cập nhật lần cuối: ngày 21 tháng 9 năm 2026',
    intro: 'Noor Passport Services (do Noor Metabolico LLC, Florida điều hành) tôn trọng quyền riêng tư của quý vị. Chính sách này giải thích chúng tôi thu thập thông tin gì, sử dụng thông tin đó như thế nào, và chia sẻ với ai khi quý vị sử dụng trang web hoặc dịch vụ chuẩn bị đơn xin hộ chiếu của chúng tôi.',
    sections: [{
      heading: 'Chúng tôi là ai',
      blocks: [{
        type: 'p',
        text: 'Noor Passport Services là một doanh nghiệp tư nhân giúp những người nói tiếng Tây Ban Nha, Creole Haiti, tiếng Anh và tiếng Việt ở tây nam Florida tự hoàn thành đơn xin hộ chiếu bằng ngôn ngữ của họ. **Chúng tôi không phải là cơ quan chính phủ, công ty luật, hay công chứng viên**, và chúng tôi không tư vấn pháp lý.'
      }]
    }, {
      heading: 'Thông tin chúng tôi thu thập',
      blocks: [{
        type: 'ul',
        items: ['**Thông tin liên hệ:** họ tên, số điện thoại, email, và ngôn ngữ quý vị muốn giao tiếp.', '**Dữ liệu đơn:** thông tin quý vị nhập vào bảng câu hỏi để chuẩn bị đơn chính thức (họ tên hợp pháp, ngày và nơi sinh, địa chỉ, và các chi tiết tương tự như trên giấy tờ tùy thân của quý vị).', '**Tài liệu quý vị tải lên:** bản sao giấy tờ tùy thân hoặc giấy chứng nhận quý vị cung cấp để chuẩn bị đơn hoặc xác thực ảnh của quý vị.', '**Ảnh hộ chiếu** (nếu quý vị mua dịch vụ bổ sung này): hình ảnh quý vị tải lên để xác thực.', '**Dữ liệu thanh toán:** thanh toán được xử lý trực tiếp bởi Stripe; Noor không lưu trữ số thẻ.', '**Dữ liệu sử dụng trang web và trợ lý:** tin nhắn quý vị gửi qua WhatsApp hoặc trợ lý ảo của trang web.']
      }]
    }, {
      heading: 'Cách chúng tôi sử dụng thông tin của quý vị',
      blocks: [{
        type: 'p',
        text: 'Chúng tôi chỉ sử dụng thông tin của quý vị để: chuẩn bị đơn quý vị yêu cầu, liên lạc với quý vị về trường hợp của quý vị, xử lý thanh toán, và xác thực ảnh hộ chiếu nếu quý vị mua dịch vụ đó. Chúng tôi không bán thông tin của quý vị cho bên thứ ba hay dùng để quảng cáo nhắm mục tiêu.'
      }]
    }, {
      heading: 'Chúng tôi chia sẻ thông tin của quý vị với ai',
      blocks: [{
        type: 'p',
        text: 'Chúng tôi chỉ chia sẻ dữ liệu với các nhà cung cấp giúp dịch vụ hoạt động được, mỗi bên tuân theo chính sách bảo mật riêng:'
      }, {
        type: 'table',
        headers: ['Nhà cung cấp', 'Mục đích sử dụng'],
        rows: [['Fillout', 'Nhận hợp đồng đã ký, thanh toán, và câu trả lời bảng câu hỏi'], ['Supabase', 'Lưu trữ an toàn dữ liệu trường hợp của quý vị (nguồn dữ liệu duy nhất của chúng tôi)'], ['Stripe', 'Xử lý thanh toán; Noor không bao giờ thấy số thẻ đầy đủ của quý vị'], ['Công cụ xác thực ảnh (ví dụ Snap2Pass)', 'Xác thực ảnh của quý vị đáp ứng quy định chính thức, nếu quý vị mua dịch vụ đó'], ['Twilio và ElevenLabs', 'Vận hành trợ lý ảo và đường dây WhatsApp']]
      }, {
        type: 'p',
        text: 'Chúng tôi không chia sẻ thông tin của quý vị với chính phủ Hoa Kỳ ngoài những gì chính quý vị nộp trong đơn của mình; Noor không nộp đơn thay mặt quý vị cho bất kỳ cơ quan nào.'
      }]
    }, {
      heading: 'Trẻ vị thành niên',
      blocks: [{
        type: 'p',
        text: 'Nhiều đơn xin hộ chiếu là dành cho trẻ em. Khi trường hợp là của trẻ vị thành niên, cha mẹ hoặc người giám hộ hợp pháp sẽ điền bảng câu hỏi và tải lên tài liệu thay mặt trẻ. Chúng tôi không thu thập thông tin trực tiếp từ trẻ vị thành niên.'
      }]
    }, {
      heading: 'Chúng tôi lưu giữ thông tin của quý vị trong bao lâu',
      blocks: [{
        type: 'p',
        text: 'Chúng tôi lưu giữ dữ liệu trường hợp của quý vị trong suốt thời gian dịch vụ và một khoảng thời gian hợp lý sau đó, phòng khi quý vị cần đến bảo đảm sửa lỗi miễn phí. Quý vị có thể yêu cầu chúng tôi xóa thông tin của mình sau khi dịch vụ hoàn tất bằng cách viết thư đến info@noorpassportservices.com.'
      }]
    }, {
      heading: 'Quyền của quý vị',
      blocks: [{
        type: 'p',
        text: 'Quý vị có thể yêu cầu chúng tôi bất cứ lúc nào: xem thông tin chúng tôi có về quý vị, sửa thông tin đó, hoặc yêu cầu chúng tôi xóa (trừ những gì chúng tôi phải lưu giữ theo luật). Hãy viết thư cho chúng tôi tại info@noorpassportservices.com bằng ngôn ngữ quý vị chọn.'
      }]
    }],
    contact: 'Noor Passport Services (DBA of Noor Metabolico LLC) — info@noorpassportservices.com'
  }
};
export function PrivacyPolicyPage() {
  const {
    language
  } = useLanguage();
  return <LegalPage doc={DOCS[language]} data-id="element-470" data-mp="2ublmsoi6jc72" />;
}